import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createRaffleSchema } from '../../utils/validation';
import { useRaffle, useUpdateRaffle } from '../../hooks/useRaffles';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import { Textarea } from '../../components/common/Textarea';
import { Select } from '../../components/common/Select';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { DRAW_SCHEDULES, PARTICIPATION_ACCESS, COUNTRIES } from '../../utils/constants';
import { utcToLocalDateTime, localDateTimeToUTC } from '../../utils/formatters';
import { CreateRaffleData } from '../../types';

export const AdminEditRaffle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useRaffle(id!);
  const updateRaffle = useUpdateRaffle();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateRaffleData>({
    resolver: zodResolver(createRaffleSchema),
  });

  useEffect(() => {
    if (data?.data) {
      const raffle = data.data;
      reset({
        name: raffle.name, description: raffle.description, ticketPrice: raffle.ticketPrice,
        currency: raffle.currency, salesStartDate: utcToLocalDateTime(raffle.salesStartDate),
        salesEndDate: utcToLocalDateTime(raffle.salesEndDate), drawSchedule: raffle.drawSchedule,
        numberOfWinners: raffle.numberOfWinners, prizeDistribution: raffle.prizeDistribution,
        organizerShare: raffle.organizerShare, participationAccess: raffle.participationAccess,
        imageUrl: raffle.imageUrl,
      });
    }
  }, [data, reset]);

  const onSubmit = async (formData: CreateRaffleData) => {
    const raffleData = { ...formData, salesStartDate: localDateTimeToUTC(formData.salesStartDate), salesEndDate: localDateTimeToUTC(formData.salesEndDate) };
    await updateRaffle.mutateAsync({ id: id!, data: raffleData });
    navigate('/admin/raffles');
  };

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  if (!data?.data) {
    return <AdminLayout><Card><p className="text-center text-gray-600 dark:text-gray-400">Raffle not found.</p></Card></AdminLayout>;
  }

  if (data.data.ticketsSold > 0) {
    return (
      <AdminLayout>
        <Card>
          <p className="text-center text-gray-600 dark:text-gray-400">This raffle cannot be edited after tickets have been sold.</p>
          <div className="flex justify-center mt-4"><Button onClick={() => navigate('/admin/raffles')}>Back to Raffles</Button></div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Edit Raffle</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Update raffle details</p>
      </div>
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Input label="Raffle Name" {...register('name')} error={errors.name?.message} required />
          <Textarea label="Description" {...register('description')} error={errors.description?.message} rows={5} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Ticket Price" type="number" step="0.01" {...register('ticketPrice', { valueAsNumber: true })} error={errors.ticketPrice?.message} required />
            <Select label="Currency" {...register('currency')} error={errors.currency?.message} options={[{ value: '', label: 'Select currency' }, ...COUNTRIES.map((c) => ({ value: c.currency, label: `${c.currency} (${c.currencySymbol})` }))]} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Sales Start Date & Time (Local)" type="datetime-local" {...register('salesStartDate')} error={errors.salesStartDate?.message} required />
            <Input label="Sales End Date & Time (Local)" type="datetime-local" {...register('salesEndDate')} error={errors.salesEndDate?.message} required />
          </div>
          <Select label="Draw Schedule" {...register('drawSchedule')} error={errors.drawSchedule?.message} options={[{ value: '', label: 'Select schedule' }, ...DRAW_SCHEDULES]} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Number of Winners" type="number" min="1" {...register('numberOfWinners', { valueAsNumber: true })} error={errors.numberOfWinners?.message} required />
            <Input label="Prize Distribution (%)" {...register('prizeDistribution')} error={errors.prizeDistribution?.message} placeholder="e.g., 50,30,20" required />
          </div>
          <Input label="Organizer Share (%)" type="number" step="0.01" min="0" max="100" {...register('organizerShare', { valueAsNumber: true })} error={errors.organizerShare?.message} required />
          <Select label="Participation Access" {...register('participationAccess')} error={errors.participationAccess?.message} options={[{ value: '', label: 'Select access type' }, ...PARTICIPATION_ACCESS]} required />
          <Input label="Image URL (Optional)" {...register('imageUrl')} error={errors.imageUrl?.message} />
          <div className="flex gap-4">
            <Button type="submit" isLoading={updateRaffle.isPending}>Update Raffle</Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin/raffles')}>Cancel</Button>
          </div>
        </form>
      </Card>
    </AdminLayout>
  );
};